import { Component, OnInit, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';
import { Usuario } from '../../common/model/usuario';
import { KeycloakService } from 'keycloak-angular';
import { KeycloakProfile } from 'keycloak-js';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { RegistroService } from 'src/app/registro.service';
import Swal from 'sweetalert2';
import { CreateRepresentanteService } from './service/create-representante.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { CargaEmpresasService } from 'src/app/services/carga-empresas.service';
import { TarjetaEmpresa } from 'src/app/carga-empresas/interface/tarjeta-empresa.interface';
import { ComercializadorService } from 'src/app/empresa/services/comercializador.service';
import { AccessControlService } from 'src/app/access-control/services/access-control.service';
import { esUsuarioInstitucional as esEmailInstitucional } from 'src/app/access-control/utils/usuario-institucional.util';

@Component({
    selector: 'app-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.css'],
})
export class TopbarComponent implements OnInit {
    selectedProfileType = '';
    empresaSeleccionada: any;
    existeEmpresa = true;
    public model = {
        usuario: {} as Usuario,
        estadoMenu: true,
    };
    public perfilUsuario?: KeycloakProfile | null = null;
    isLogueado: boolean;
    private readonly apiUrl: string = environment.apiSiriUrl;
    empresasAsociadas: TarjetaEmpresa[];
    idEmpresaSelected;
    tienePermisos = false;
    userEmail: string | null = null;

    get esUsuarioInstitucional(): boolean {
        return esEmailInstitucional(this.userEmail);
    }

    constructor(
        private readonly router: Router,
        private readonly renderer: Renderer2,
        protected readonly keycloak: KeycloakService,
        private readonly http: HttpClient,
        private readonly registroService: RegistroService,
        private readonly createRService: CreateRepresentanteService,
        private readonly cargaEmpresasService: CargaEmpresasService,
        private readonly comercializadorService: ComercializadorService,
        private readonly accessControlService: AccessControlService
    ) {}

    private readonly suscripciones: Subscription = new Subscription();

    async ngOnInit(): Promise<void> {
        this.configurarSuscripciones();
        await this.manejarFlujoUsuario();
    }

    private configurarSuscripciones(): void {
        this.suscripciones.add(
            this.registroService.empresaSelected$.subscribe(
                (empresaSeleccionada) => {
                    this.actualizarEmpresaSeleccionada();
                }
            )
        );

        this.suscripciones.add(
            this.registroService.listaEmpresasAsociadas$.subscribe(() => {
                this.actualizarEmpresaSeleccionada();
            })
        );
    }

    private actualizarEmpresaSeleccionada(): void {
        this.empresasAsociadas = this.registroService.listaEmpresasAsociadas;
        const empresaSelectedId = this.registroService.empresaSelected;
        const empresaEncontrada =
            this.registroService.listaEmpresasAsociadas?.find(
                (emp) => String(emp.id) === String(empresaSelectedId)
            );

        if (empresaEncontrada) {
            this.empresaSeleccionada = empresaEncontrada.razonSocial;
            this.registroService.correoCorporativo =
                empresaEncontrada.correo_corporativo;
            this.registroService.eslabonEmpresaSelected =
                empresaEncontrada.eslabon;
        } else {
            this.empresaSeleccionada = 'Empresa';
        }
    }

    private async manejarFlujoUsuario(): Promise<void> {
        try {
            await this.inicializarSesionUsuario();
        } catch (error) {
            console.error('Error en el flujo del usuario:', error);
        }
    }

    private async inicializarSesionUsuario(): Promise<void> {
        this.isLogueado = await this.keycloak.isLoggedIn();
        const rolesUsuario = await this.keycloak.getUserRoles();
        const esAdministrador = rolesUsuario.includes('Administrador (UPRA)');

        await this.createRService.obtenerPerfilUsuario();
        this.userEmail = this.accessControlService.obtenerEmailUsuario();

        if (!this.isLogueado) return;

        this.perfilUsuario =
            this.perfilUsuario ?? (await this.keycloak.loadUserProfile());
        try {
            await this.obtenerDatosRepresentante();
        } catch {
            // El representante puede no existir aún en BD
        }

        this.tienePermisos = await this.accessControlService.validarPermisos();
        if (!this.tienePermisos) {
            await Swal.fire({
                icon: 'info',
                title: 'Actualización requerida',
                text: 'Tu usuario se encuentra a la espera de asignación de permisos para poder acceder correctamente al sistema',
                confirmButtonText: 'Aceptar',
            });
            return;
        }

        if (esAdministrador) {
            this.manejarFlujoAdministrador();
            return;
        }

        await this.manejarFlujoUsuarioRegular(esAdministrador);
    }

    private manejarFlujoAdministrador(): void {
        this.router.navigate(['/inicio']);
        this.registroService.empresasAsociadas = true;
    }

    private async manejarFlujoUsuarioRegular(esAdmin: boolean): Promise<void> {
        this.perfilUsuario = await this.keycloak.loadUserProfile();

        try {
            const respuesta = await this.obtenerDatosRepresentante();
            await this.procesarRepresentanteUsuario(respuesta, esAdmin);
        } catch (error) {
            await this.manejarErrorRepresentante(esAdmin, error);
        }
    }

    private async obtenerDatosRepresentante(): Promise<any> {
        const respuesta = await firstValueFrom(
            this.http.get<any>(
                `${this.apiUrl}representante-agente-vigilado/Keycloack/${this.perfilUsuario.id}`
            )
        );
        localStorage.setItem(
            'representanteAgenteVigilado',
            JSON.stringify(respuesta)
        );
        this.registroService.representanteAgenteVigilado = respuesta;
        this.userEmail = this.accessControlService.obtenerEmailUsuario();
        return respuesta;
    }

    private async procesarRepresentanteUsuario(
        respuesta: any,
        esAdmin: boolean
    ): Promise<void> {
        if (respuesta.estado === 'Activo') {
            await this.manejarUsuarioActivo();
        } else if (respuesta.estado === 'Desactivado') {
            this.manejarUsuarioDesactivado();
        } else {
            this.manejarOtroEstadoUsuario();
        }
    }

    private async manejarUsuarioActivo(): Promise<void> {
        this.registroService.empresasAsociadas = true;
        this.registroService.listaEmpresasAsociadas = await firstValueFrom(
            this.cargaEmpresasService.obtenerEmpresas()
        );
        this.empresasAsociadas = this.registroService.listaEmpresasAsociadas;

        try {
            await this.manejarNavegacionEmpresas();
        } catch (error) {
            console.error('Error al manejar navegación de empresas:', error);
        }
    }

    private async manejarNavegacionEmpresas(): Promise<void> {
        if (!this.empresasAsociadas?.length) {
            this.navegarANuevaEmpresa();
        } else if (this.empresasAsociadas.length > 1) {
            this.navegarASeleccionEmpresas();
        } else {
            this.navegarAInicioConEmpresaUnica();
        }
    }

    private navegarANuevaEmpresa(): void {
        if (this.esUsuarioInstitucional) {
            this.router.navigate(['/inicio']);
            return;
        }
        this.router.navigate(['/nueva-empresa']);
        this.existeEmpresa = false;
        this.registroService.empresasAsociadas = false;
    }

    private navegarASeleccionEmpresas(): void {
        if (this.tienePermisos === true && !this.esUsuarioInstitucional) {
            this.router.navigate(['/cargar-empresas']);
        } else {
            this.router.navigate(['/inicio']);
        }
        this.registroService.empresasAsociadas = false;
    }

    private navegarAInicioConEmpresaUnica(): void {
        this.router.navigate(['/inicio']);
        this.registroService.empresasAsociadas = true;
        this.registroService.nitEmpresaSelected = this.empresasAsociadas[0].nit;
        this.registroService.empresaSelected = this.empresasAsociadas[0].id;
        this.comercializadorService
            .findByAgenteVigiladoId(this.empresasAsociadas[0].id)
            .subscribe((caracterizacionComercializador) => {
                this.registroService.caracterizacionComercializador =
                    caracterizacionComercializador;
            });
    }

    private manejarUsuarioDesactivado(): void {
        this.registroService.empresasAsociadas = true;
        Swal.fire(
            '',
            'Este usuario no cuenta con los permisos para ingresar al sistema, comuníquese con un administrador al correo: gestionsiriiagro@upra.gov.co.',
            'info'
        );
        setTimeout(() => this.logOut(), 10000);
    }

    private manejarOtroEstadoUsuario(): void {
        this.router.navigate(['/nueva-empresa']);
        this.registroService.empresasAsociadas = false;
    }

    private async manejarErrorRepresentante(
        esAdmin: boolean,
        error: any
    ): Promise<void> {
        if (!esAdmin) {
            if (this.esUsuarioInstitucional) {
                this.router.navigate(['/inicio']);
                return;
            }
            this.router.navigate(['/nueva-empresa']);
            await this.createRService.createRepresentante();
            this.registroService.empresasAsociadas = false;
            console.error('Error al cargar usuario de Keycloak:', error);
        }
    }

    ngOnDestroy(): void {
        this.suscripciones.unsubscribe();
    }

    async logOut(): Promise<void> {
        try {
            localStorage.removeItem('userRol');
            localStorage.removeItem('empresas-asociadas');
            localStorage.removeItem('usuarioKeycloak');
            // Limpiar el estado de sesión (empresa seleccionada, eslabón, rol,
            // listas, etc.) para no conservarlo en el próximo inicio de sesión.
            this.registroService.limpiarEstadoSesion();
            await this.router.navigate(['/inicio']);
            await this.keycloak.logout();
        } catch (error) {
            console.error('Error al cerrar sesión:', error);
        }
    }

    mostrarMenu() {
        console.error('ENTRA O No ENTRA');
        if (this.model.estadoMenu) {
            this.model.estadoMenu = false;
            this.renderer.addClass(document.body, 'sidebar-collapse');
        } else {
            this.model.estadoMenu = true;
            this.renderer.removeClass(document.body, 'sidebar-collapse');
            this.renderer.removeClass(document.body, 'layout-top-nav');
        }
    }

    ingresar(empresa) {
        this.registroService.empresaSelected = empresa?.id;
        this.registroService.nitEmpresaSelected = empresa?.nit;
        this.registroService.eslabonEmpresaSelected = empresa?.eslabon;
        this.empresaSeleccionada = empresa?.razonSocial;
        this.registroService.correoCorporativo = empresa?.correo_corporativo;
        this.registroService.empresasAsociadas = true;
        this.existeEmpresa = true;
        this.registroService.caracterizacionComercializador = null;
        this.comercializadorService
            .findByAgenteVigiladoId(empresa?.id)
            .subscribe((caracterizacionComercializador) => {
                this.registroService.caracterizacionComercializador =
                    caracterizacionComercializador;
            });
        this.router.navigate(['/inicio'], {
            queryParams: { refresh: Date.now() },
        });
    }
}
